import React from 'react';
import { LikeDislikeGroup } from 'common/components/like-dislike-group/like-dislike-group';
import { TilesLineGallery } from 'common/components/media-gallery/tiles-line-gallery';
import { Review, ReviewAuthorContent, ReviewRatingScore } from 'common/components/review';
import { ReviewAnswer } from 'common/components/review-answer/review-answer';
import { PictureSources, ReviewData, LikeDislikeOpinion } from 'features/reviews';
import { getLang, jc } from 'utils';
import { Breakpoint } from 'types/utils';
import { ReviewContentFormatter } from 'common/components/review-content-formatter';
import { reviewCard, reviewAnswer } from '../styles';
import { TileContent } from './tile-content';
import { getRandomInteger } from './utils';
import { ReviewAdditionalInfo } from 'common/components/review/review-additional-info';

const AVATAR_COLORS = ['green', 'blue', 'purple', 'red', 'orange', 'turquoise'] as const;

export const ReviewCard: React.FC<{
  reviewData: ReviewData;
  reviewOpinion: LikeDislikeOpinion | null;
  className?: string;
  breakpoint: Breakpoint;
  onShowPicture: (pic: PictureSources | null) => void;
  onLike: (reviewsId: string) => void;
  onCancelLike: (reviewsId: string) => void;
  onCancelDislike: (reviewsId: string) => void;
  onDislike: (reviewsId: string) => void;
  onChangeDislikeToLike: (reviewsId: string) => void;
  onChangeLikeToDislike: (reviewsId: string) => void;
}> = ({
  reviewData,
  breakpoint,
  reviewOpinion,
  className = '',
  onShowPicture,
  onLike,
  onDislike,
  onCancelLike,
  onCancelDislike,
  onChangeDislikeToLike,
  onChangeLikeToDislike,
}) => {
  const locale = getLang();

  const optionalProps = {
    pros: isNonEmtyString(reviewData.pros) ? (
      <ReviewContentFormatter data-qa="review-card-pros_mf-pdp">{reviewData.pros}</ReviewContentFormatter>
    ) : undefined,

    cons: isNonEmtyString(reviewData.cons) ? (
      <ReviewContentFormatter data-qa="review-card-cons_mf-pdp">{reviewData.cons}</ReviewContentFormatter>
    ) : undefined,

    photos:
      reviewData.photos instanceof Array && reviewData.photos.length > 0 ? (
        <TilesLineGallery
          data-qa="photos-at-reviews-card_mf-pdp"
          breakpoint={breakpoint}
          tilesContent={reviewData.photos.map((picture) => (
            <TileContent
              key={`${picture.reviewId}-${picture.urlOriginal}`}
              picture={picture}
              onShowPicture={onShowPicture}
            />
          ))}
        />
      ) : undefined,
  };

  const likesCount = reviewOpinion === 'LIKE' ? reviewData.likes + 1 : reviewData.likes;
  const dilikesCount = reviewOpinion === 'DISLIKE' ? reviewData.dislikes + 1 : reviewData.dislikes;

  const onLikeClickCb = React.useCallback(() => {
    const likeCallback = chooseLikeDislikeCallback(
      'LIKE',
      reviewOpinion,
      onLike,
      onCancelLike,
      onChangeDislikeToLike,
    );
    likeCallback(reviewData.reviewId);
  }, [reviewOpinion, reviewData.reviewId, onLike, onCancelLike, onChangeDislikeToLike]);

  const onDislikeClickCb = React.useCallback(() => {
    let dislikeCallback = chooseLikeDislikeCallback(
      'DISLIKE',
      reviewOpinion,
      onDislike,
      onCancelDislike,
      onChangeLikeToDislike,
    );
    dislikeCallback(reviewData.reviewId);
  }, [reviewOpinion, reviewData.reviewId, onDislike, onCancelDislike, onChangeLikeToDislike]);

  const createdDate = new Date(reviewData.createdAt || '');

  return (
    <>
      <Review
        data-qa="review-card_mf-pdp"
        className={jc(reviewCard, className)}
        author={
          <ReviewAuthorContent
            authorName={reviewData.author.name}
            authorInitials={reviewData.author.initials}
            authorAvatarUrl={reviewData.author.avatarUrl}
            locale={locale}
            avatarThemeColor={AVATAR_COLORS[getRandomInteger(AVATAR_COLORS.length - 1)]}
            date={createdDate}
          />
        }
        ratingScore={<ReviewRatingScore ratingValue={reviewData.ratingScore} />}
        additionalInfo={
          <ReviewAdditionalInfo
            data-qa="review-card-additional-info_mf-pdp"
            date={createdDate}
            isVerified={!!reviewData.author.additionalInfo.isVerified}
            durationOfUse={reviewData.author.additionalInfo.durationOfUse}
          />
        }
        text={
          <ReviewContentFormatter data-qa="review-card-body_mf-pdp">
            {reviewData.body}
          </ReviewContentFormatter>
        }
        likeDislikeGroup={
          <LikeDislikeGroup
            data-qa="review-like-dislike-pdp"
            likes={likesCount}
            dislikes={dilikesCount}
            opinion={reviewOpinion}
            onLike={onLikeClickCb}
            onDislike={onDislikeClickCb}
          />
        }
        {...optionalProps}
      />

      {typeof reviewData.comments !== 'undefined' &&
        reviewData.comments.map((comment) => {
          return (
            <ReviewAnswer
              data-qa="review-answer-card_mf-pdp"
              key={comment.commentId}
              className={reviewAnswer}
              answererName={comment.author.name}
              answererInitials={comment.author.initials}
              answererOccupation={comment.author.occupation}
              answererAvatarUrl={comment.author.avatarUrl}
              text={
                <ReviewContentFormatter data-qa="review-answer-card-body_mf-pdp">
                  {comment.body}
                </ReviewContentFormatter>
              }
            />
          );
        })}
    </>
  );
};

const isNonEmtyString = (str?: string) => {
  return typeof str === 'string' && str.length > 0;
};

const chooseLikeDislikeCallback = (
  baseValue: LikeDislikeOpinion,
  opinion: LikeDislikeOpinion | null,
  onNull: (reviewsId: string) => void,
  onSameValue: (reviewsId: string) => void,
  onOppositeValue: (reviewsId: string) => void,
) => {
  let result = onOppositeValue;

  if (opinion === null) {
    result = onNull;
  } else if (opinion === baseValue) {
    result = onSameValue;
  }

  return result;
};
